import { useState } from 'react';
import { endOfDay, isBefore } from 'date-fns';
import { useLang } from 'hooks/lang';

import { actions, projectStatusConfig, getProjectFinishDate, isOwner } from 'entities';

import { Alert, DatePicker, Column, Row, Label, Button } from 'components/Blocks';
import { DragItem, DragList, DropResult } from 'components/DragList';

import { CirclePlayIcon, DragHorizontalIcon } from 'components/Icons';

import { TaskCards } from './TaskCards';

import { useUser } from 'hooks';
import { useProjectProfile } from '../context';
import { useToast } from 'providers/Toast';

import { getReadableDate, getLocalDateString } from 'utils/date';
import { MESSAGES } from '../utils';

import { cn } from 'styles';

export function StartProject() {
  const loggedUser = useUser();

  const { addToast } = useToast();

  const { project, fullBlocked, updateProject } = useProjectProfile();

  const [dates, setDates] = useState<Record<string, Date | undefined>>({});

  const { t } = useLang();

  const userIsPM = isOwner(project, loggedUser);

  const estimatedFinish = getProjectFinishDate(project.estimatedCompletionWorkingDays);
  const expectedDateText = getReadableDate(estimatedFinish);

  const changeDate = (id: string, date?: Date): void => {
    setDates((old) => ({
      ...old,
      [id]: date,
    }));
  };

  const shouldDisableDate = (date: Date | null): boolean => {
    if (!date) return false;

    return isBefore(endOfDay(estimatedFinish), date);
  };

  const allDatesOk = project.tasks.every(({ id }) => {
    const date = dates[id];

    if (!date) return false;

    return !isBefore(endOfDay(estimatedFinish), date);
  });

  const startProject = actions.projects.startProject.use({
    block: !allDatesOk,

    params: {
      id: project.id,
      taskDates: Object.fromEntries(
        Object.entries(dates).map(([id, date]) => [id, getLocalDateString(date as Date)]),
      ),
    },

    onSuccess: (_, { taskDates }) => {
      updateProject((old) => ({
        ...old,
        status: projectStatusConfig.started.code,
        tasks: old.tasks.map(({ id, ...task }) => ({
          ...task,
          id,
          expectedDate: taskDates[id],
        })),
      }));

      addToast(MESSAGES.startSuccess(t, expectedDateText));
    },
  });

  const [displayTasks, setDisplayTasks] = useState([...project.tasks]);

  const reorder = (startIndex: number, endIndex: number): void => {
    const newOrder = [...displayTasks];

    const [removed] = newOrder.splice(startIndex, 1);
    newOrder.splice(endIndex, 0, removed);

    setDisplayTasks(newOrder);
  };

  const onDragEnd = (result: DropResult): void => {
    const { destination, source } = result;

    if (!destination) return;

    reorder(source.index, destination.index);
  };

  return (
    <>
      <Column>
        {!project.authorizedToStart && (
          <Alert
            className="mx-auto w-fit animate-in fade-in-0 slide-in-from-bottom-12"
            level="warning"
          >
            {t('project-still-has-not-been-approved-to-start just-yet')}
          </Alert>
        )}

        <Column
          className="mx-auto w-fit rounded-lg border border-dashed p-4 text-center phone:px-0"
          alignItems="center"
          justifyContent="center"
        >
          <h4>{t('project-not-started')}</h4>

          {userIsPM ? (
            <Column gap="6">
              <h6 className="text-center">{t('please-define-tasks-expected-dates')}</h6>

              <Column gap="2">
                <Row justifyContent="space-between">
                  <Label>{t('estimated-working-days')}</Label>

                  <h4>{project.estimatedCompletionWorkingDays}</h4>
                </Row>

                <span className="text-center text-sm">
                  {t('estimated-completed-date-will-be')} <strong>{expectedDateText}</strong>
                </span>
              </Column>

              <h6>{t('tasks')}</h6>

              <DragList
                onDragEnd={onDragEnd}
                droppableId="droppable"
                // to prevent the container to reduce in height
                style={{ minHeight: 50 * displayTasks.length }}
              >
                {displayTasks.map(({ name, id }, index) => (
                  <DragItem
                    key={id}
                    draggableId={id}
                    index={index}
                    className={(snapshot) =>
                      cn('cursor-grab rounded-lg p-1', snapshot.isDragging && 'bg-hover')
                    }
                  >
                    <Row justifyContent="space-between">
                      <Row className="text-lg font-medium">
                        <DragHorizontalIcon />

                        {name}
                      </Row>

                      <DatePicker
                        onSelect={(date) => changeDate(id, date)}
                        selected={dates[id] || undefined}
                        // allowRemoval
                        disablePast
                        disabled={!project.authorizedToStart || shouldDisableDate}
                      />
                    </Row>
                  </DragItem>
                ))}
              </DragList>

              <Button
                disabled={!project.authorizedToStart || fullBlocked || !allDatesOk}
                {...startProject.buttonProps}
              >
                <CirclePlayIcon />
                {t('start-project')}
              </Button>
            </Column>
          ) : (
            <p>{t('project-manager-needs-to-start-it-before-you-can-access-it')}</p>
          )}

          {loggedUser.team.canEnableProjectToStart && !project.authorizedToStart && (
            <Button to="pendingActions" params={{ tab: 'projectStartAuthorization' }}>
              {t('authorize-project-to-start')}
            </Button>
          )}
        </Column>
      </Column>

      {!userIsPM && <TaskCards disabled />}
    </>
  );
}
